import { useEffect, useRef } from "react";
import { Button, Modal, Spinner } from "@cntrl/ui";
import { useReissueInviteLink, type ReissueTarget } from "../../app/hooks/useMyInvites.js";
import { InviteLinkShare } from "./InviteLinkShare.js";
import styles from "./InviteLinkDialog.module.scss";

export interface InviteLinkDialogProps {
  target: ReissueTarget;
  /** Who it is for — the sentence names them. */
  recipient?: string | null;
  onClose: () => void;
}

/**
 * "Copy link" on a pending invite (ART-239). We hold only the token's HASH, so the original link can never be
 * shown again: opening this mints a fresh one (the old link dies, the expiry restarts) and shows it ready to copy
 * or share. A dialog rather than a straight-to-clipboard button because the clipboard write would land after a
 * network round trip, which Safari refuses as no longer being part of the press.
 */
export function InviteLinkDialog({ target, recipient, onClose }: InviteLinkDialogProps) {
  const reissue = useReissueInviteLink();
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;   // once per open — StrictMode double-invokes effects in dev
    fired.current = true;
    reissue.mutate(target);
  }, [reissue, target]);

  return (
    <Modal
      title="Invite link"
      size="sm"
      onClose={onClose}
      footer={
        <Button type="button" onClick={onClose}>
          Done
        </Button>
      }
    >
      {reissue.isPending || reissue.isIdle ? (
        <div className={styles.center} role="status" aria-live="polite">
          <Spinner label="Getting a fresh link…" />
        </div>
      ) : reissue.isError ? (
        <p className={styles.error} role="alert">
          {reissue.error.message || "Couldn't get a link. Try again."}
        </p>
      ) : (
        <div className={styles.body}>
          <p className={styles.lead}>This replaces the previous link — the old one no longer works.</p>
          <InviteLinkShare url={reissue.data?.joinUrl ?? ""} expiresOn={reissue.data?.expiresOn} recipient={recipient} />
        </div>
      )}
    </Modal>
  );
}
